
import { useEffect, useState } from "react";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";
import { ProfileBreadcrumbs } from "./components/ProfileBreadcrumbs";
import { ProfilePreviewButton } from "./components/ProfilePreviewButton";
import { ProfileContent } from "./components/ProfileContent";
import { useTranslations } from "@/hooks/useTranslations";

const defaultPredefinedTypes = ['Musculação', 'Perda de Peso', 'Cardio', 'CrossFit', 'Pilates', 'Yoga', 'Treino Funcional', 'Personal Training'];

export const Profile = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const { t } = useTranslations("profile");
  const [loading, setLoading] = useState(false);
  const [predefinedTypes, setPredefinedTypes] = useState<string[]>(defaultPredefinedTypes);
  const [profile, setProfile] = useState({
    firstName: "",
    lastName: "",
    avatarUrl: "",
    publicUrl: "",
    gymPhotoUrl: "",
    trainingTypes: [] as string[],
    aboutMe: "",
    specialties: [] as string[],
    instagramUrl: "",
    facebookUrl: "",
    experienceYears: "",
    hourlyRate: "",
    showPrices: false
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const {
          data: profileData,
          error: profileError
        } = await supabase.from('profiles').select('first_name, last_name, avatar_url, public_url, gym_photo_url, training_types, about_me, specialties, instagram_url, facebook_url, experience_years, hourly_rate, show_prices, predefined_training_types').eq('id', user?.id).single();
        if (profileError) throw profileError;
        if (profileData) {
          setProfile({
            firstName: profileData.first_name || "",
            lastName: profileData.last_name || "",
            avatarUrl: profileData.avatar_url || "",
            publicUrl: profileData.public_url || "",
            gymPhotoUrl: profileData.gym_photo_url || "",
            trainingTypes: profileData.training_types || [],
            aboutMe: profileData.about_me || "",
            specialties: profileData.specialties || [],
            instagramUrl: profileData.instagram_url || "",
            facebookUrl: profileData.facebook_url || "",
            experienceYears: profileData.experience_years?.toString() || "",
            hourlyRate: profileData.hourly_rate?.toString() || "",
            showPrices: profileData.show_prices || false
          });
          if (profileData.predefined_training_types && Array.isArray(profileData.predefined_training_types)) {
            setPredefinedTypes(profileData.predefined_training_types);
          }
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        toast({
          title: "Erro",
          description: "Não foi possível carregar os dados do perfil.",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };
    if (user) {
      fetchProfile();
    }
  }, [user, toast]);

  const handleUpdateProfile = async () => {
    try {
      setLoading(true);
      const {
        error
      } = await supabase.from('profiles').update({
        public_url: profile.publicUrl,
        gym_photo_url: profile.gymPhotoUrl,
        training_types: profile.trainingTypes,
        about_me: profile.aboutMe,
        specialties: profile.specialties,
        instagram_url: profile.instagramUrl,
        facebook_url: profile.facebookUrl,
        experience_years: profile.experienceYears ? parseInt(profile.experienceYears) : null,
        hourly_rate: profile.hourlyRate ? parseFloat(profile.hourlyRate) : null,
        show_prices: profile.showPrices
      }).eq('id', user?.id);
      if (error) throw error;
      toast({
        title: t("profileUpdated"),
        description: t("profileUpdatedDesc")
      });
    } catch (error) {
      toast({
        title: t("profileError"),
        description: t("profileErrorDesc"),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (updates: Partial<typeof profile>) => {
    setProfile(prev => ({
      ...prev,
      ...updates
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <ProfileBreadcrumbs />
        <ProfilePreviewButton publicUrl={profile.publicUrl} />
      </div>

      <Card className="bg-gray-50 dark:bg-gray-900/70 border-slate-200 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="text-slate-900 dark:text-white">{t("title")}</CardTitle>
          <CardDescription className="text-slate-600 dark:text-gray-300">
            {t("subtitle")}
          </CardDescription>
        </CardHeader>
        <ProfileContent 
          userId={user?.id || ""} 
          profile={profile} 
          loading={loading} 
          predefinedTypes={predefinedTypes} 
          onProfileChange={handleProfileChange} 
          onSave={handleUpdateProfile} 
        />
      </Card>
    </div>
  );
};
